// Pure filter/sort/search logic for a coach's client roster. No React, no
// ambient clock — `now` is injected so every branch is testable, following
// `insightDerive.js` and `applyRosterTags.js`.
//
// SCOPE: these predicates read only what `GET /oversight/coaches/{id}/clients`
// returns today — status, frozen state, last activity, message count, CSAT.
// The design's plan-based chips (stale plan, no plan, not tracking, new) need
// `/fetch-coach-client-insights` scoped to another coach, which the backend
// does not support yet; they are deliberately absent rather than approximated.

/** A client with no activity for this many days reads as quiet. */
export const QUIET_AFTER_DAYS = 7;

const MS_PER_DAY = 86400000;

export const CLIENT_FILTER_IDS = {
  AWAITING: "awaiting",
  QUIET: "quiet",
  NOT_TRACKING: "notTracking",
  NEVER: "never",
  FROZEN: "frozen",
  PAUSED: "paused",
};

/**
 * Whole days since an ISO timestamp, both ends floored to UTC midnight so the
 * number does not slide by one as the day wears on.
 *
 * @param {string|null|undefined} iso
 * @param {Date} [now]
 * @returns {number|null} Null when the timestamp is missing or unparseable.
 */
export function daysSinceActivity(iso, now = new Date()) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const then = new Date(ms);
  const thenMidnight = Date.UTC(then.getUTCFullYear(), then.getUTCMonth(), then.getUTCDate());
  const todayMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((todayMidnight - thenMidnight) / MS_PER_DAY);
}

/** True when the coach and client have never exchanged anything in this room. */
export function isNeverMessaged(client) {
  return !client?.last_activity_at || (client?.message_count ?? 0) === 0;
}

/** @type {Record<string, {id: string, label: string, matches: (c: Object, ctx: Object) => boolean}>} */
export const CLIENT_FILTER_DEFS = {
  [CLIENT_FILTER_IDS.AWAITING]: {
    id: CLIENT_FILTER_IDS.AWAITING,
    label: "Awaiting reply",
    // Server-computed: the newest human message in the room is the client's.
    matches: (c) => c?.awaiting_coach_reply === true,
  },
  [CLIENT_FILTER_IDS.QUIET]: {
    id: CLIENT_FILTER_IDS.QUIET,
    label: "Gone quiet",
    // "Never messaged" is its own chip and a different job, so it is excluded
    // here rather than swept in — a room that never started has not gone quiet.
    //
    // Measured from the last COACH message where the endpoint supplies one:
    // a client who writes daily and is never answered has recent activity but
    // has heard nothing, which is exactly who this chip is for. Falls back to
    // last_activity_at against an older backend.
    matches: (c, { now, quietAfterDays }) => {
      if (isNeverMessaged(c)) return false;
      const days = daysSinceActivity(c.last_coach_message_at ?? c.last_activity_at, now);
      return days != null && days >= quietAfterDays;
    },
  },
  [CLIENT_FILTER_IDS.NOT_TRACKING]: {
    id: CLIENT_FILTER_IDS.NOT_TRACKING,
    label: "Not tracking",
    // Strictly zero logged days, and only when the field is present. A missing
    // count is unknown, not zero — an older backend must not sweep the whole
    // roster into this chip.
    matches: (c) => c?.food_log_days === 0,
  },
  [CLIENT_FILTER_IDS.NEVER]: {
    id: CLIENT_FILTER_IDS.NEVER,
    label: "Never messaged",
    matches: (c) => isNeverMessaged(c),
  },
  [CLIENT_FILTER_IDS.FROZEN]: {
    id: CLIENT_FILTER_IDS.FROZEN,
    label: "Frozen",
    matches: (c) => Boolean(c?.frozen_at),
  },
  [CLIENT_FILTER_IDS.PAUSED]: {
    id: CLIENT_FILTER_IDS.PAUSED,
    label: "Paused",
    matches: (c) => String(c?.status || "").toLowerCase() === "paused",
  },
};

export const ALL_CLIENT_FILTER_IDS = Object.values(CLIENT_FILTER_IDS);

/**
 * Toggle one filter id in a selection set. Multi-select and ANDed, matching how
 * `toggleFilter` behaves for the chat rosters.
 *
 * @param {string[]} selected
 * @param {string} id
 * @returns {string[]} A new array; the input is never mutated.
 */
export function toggleClientFilter(selected, id) {
  const current = Array.isArray(selected) ? selected : [];
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

/**
 * Whether a client passes every selected filter.
 *
 * @param {Object} client
 * @param {string[]} selected
 * @param {Object} [options]
 * @param {Date} [options.now]
 * @param {number} [options.quietAfterDays]
 * @returns {boolean}
 */
export function matchesClientFilters(client, selected, options = {}) {
  const ids = Array.isArray(selected) ? selected : [];
  if (ids.length === 0) return true;
  const ctx = {
    now: options.now || new Date(),
    quietAfterDays: options.quietAfterDays ?? QUIET_AFTER_DAYS,
  };
  return ids.every((id) => CLIENT_FILTER_DEFS[id]?.matches(client, ctx) ?? true);
}

/**
 * How many clients each filter would match on its own — the chip counts.
 * Counted against the searched list but NOT against the other filters, so a
 * chip never reads zero merely because a different chip is active.
 *
 * @param {Array<Object>} clients
 * @param {Object} [options] - `{ now, quietAfterDays }`.
 * @returns {Record<string, number>}
 */
export function countClientFilterMatches(clients, options = {}) {
  const list = Array.isArray(clients) ? clients : [];
  const ctx = {
    now: options.now || new Date(),
    quietAfterDays: options.quietAfterDays ?? QUIET_AFTER_DAYS,
  };
  return ALL_CLIENT_FILTER_IDS.reduce((acc, id) => {
    acc[id] = list.filter((c) => CLIENT_FILTER_DEFS[id].matches(c, ctx)).length;
    return acc;
  }, {});
}

/**
 * Case-insensitive search over the client's display name and id.
 *
 * @param {Array<Object>} clients
 * @param {string} query
 * @param {(clientId: string) => string|null|undefined} nameOf - Resolver from `useCommsIdentity`.
 * @returns {Array<Object>}
 */
export function searchClients(clients, query, nameOf) {
  const list = Array.isArray(clients) ? clients : [];
  const q = String(query || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter((c) => {
    const name = nameOf?.(c.client_id) || "";
    return (
      name.toLowerCase().includes(q) ||
      String(c.display_id ?? "").toLowerCase().includes(q) ||
      String(c.client_id ?? "").toLowerCase().includes(q)
    );
  });
}

export const CLIENT_SORT_IDS = {
  NEGLECTED: "neglected",
  TRACKING: "tracking",
  RECENT: "recent",
  MESSAGES: "messages",
  CSAT: "csat",
  NAME: "name",
};

export const CLIENT_SORT_OPTIONS = [
  { id: CLIENT_SORT_IDS.NEGLECTED, label: "Most neglected" },
  { id: CLIENT_SORT_IDS.TRACKING, label: "Least tracking" },
  { id: CLIENT_SORT_IDS.RECENT, label: "Most recent" },
  { id: CLIENT_SORT_IDS.MESSAGES, label: "Most messages" },
  { id: CLIENT_SORT_IDS.CSAT, label: "Lowest CSAT" },
  { id: CLIENT_SORT_IDS.NAME, label: "Name" },
];

/** Sortable rank for "most neglected". Never-messaged outranks any real silence. */
function neglectRank(client, now) {
  if (isNeverMessaged(client)) return Number.POSITIVE_INFINITY;
  // Prefer silence from the coach over room activity — see the QUIET filter.
  return daysSinceActivity(client.last_coach_message_at ?? client.last_activity_at, now) ?? -1;
}

/** Last activity as an epoch, or -Infinity so unknowns sink under "Most recent". */
function activityEpoch(client) {
  const ms = Date.parse(client?.last_activity_at || "");
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Sort a roster. Returns a new array; the input is never mutated.
 *
 * Ties break on client_id so the order is stable across renders — without it,
 * a roster where most clients share a rank would reshuffle on every poll.
 *
 * @param {Array<Object>} clients
 * @param {string} sortId - One of `CLIENT_SORT_IDS`.
 * @param {Object} [options]
 * @param {Date} [options.now]
 * @param {(clientId: string) => string|null|undefined} [options.nameOf]
 * @returns {Array<Object>}
 */
export function sortClients(clients, sortId, options = {}) {
  const list = Array.isArray(clients) ? [...clients] : [];
  const now = options.now || new Date();
  const nameOf = options.nameOf || (() => "");

  const tie = (a, b) => String(a.client_id ?? "").localeCompare(String(b.client_id ?? ""));

  const comparators = {
    [CLIENT_SORT_IDS.NEGLECTED]: (a, b) =>
      neglectRank(b, now) - neglectRank(a, now) || tie(a, b),
    [CLIENT_SORT_IDS.RECENT]: (a, b) => activityEpoch(b) - activityEpoch(a) || tie(a, b),
    [CLIENT_SORT_IDS.MESSAGES]: (a, b) =>
      (b.message_count ?? 0) - (a.message_count ?? 0) || tie(a, b),
    // Unrated clients sort last: "lowest CSAT" means a bad score, not a missing one.
    [CLIENT_SORT_IDS.CSAT]: (a, b) =>
      (a.avg_csat ?? Number.POSITIVE_INFINITY) - (b.avg_csat ?? Number.POSITIVE_INFINITY) ||
      tie(a, b),
    // Unknown log counts sort last: absent data is not evidence of no tracking.
    [CLIENT_SORT_IDS.TRACKING]: (a, b) =>
      (a.food_log_days ?? Number.POSITIVE_INFINITY) -
        (b.food_log_days ?? Number.POSITIVE_INFINITY) || tie(a, b),
    [CLIENT_SORT_IDS.NAME]: (a, b) =>
      String(nameOf(a.client_id) || "").localeCompare(String(nameOf(b.client_id) || "")) ||
      tie(a, b),
  };

  const cmp = comparators[sortId] || comparators[CLIENT_SORT_IDS.NEGLECTED];
  return list.sort(cmp);
}
